/**
 * AuthContext - Wristband Authentication
 *
 * @description
 * Manages authentication state for the application using Wristband OAuth tokens.
 *
 * Key features:
 * - Token storage: Access token in sessionStorage, refresh token in httpOnly cookie
 * - Auto-refresh: Automatically refreshes token when expired or on 401
 * - Session persistence: Checks for existing session on mount
 * - User context: Provides user, company, and employee info
 * - Multi-company: Supports users with multiple company associations via companies[]
 * - Company switching: switchCompany() updates context + persists to localStorage
 *
 * @dependencies
 * - apiClient: HTTP requests to backend
 * - Backend auth endpoints: /api/auth/me, /api/auth/refresh, /api/auth/logout
 *
 * @notes
 * - Access tokens stored in sessionStorage (survives refresh, not tab close)
 * - Pack SO live company scope is sessionStorage (same-tab reload). New login
 *   / new tab apply users.default_*. Star does not write this key.
 * - Refresh tokens are in httpOnly cookies (handled by browser)
 * - Token refresh happens automatically on 401 responses
 * - Cross-tab refresh uses navigator.locks so only one tab rotates the
 *   refresh token. Waiting tabs reuse the BroadcastChannel message. The
 *   access token is not stored in localStorage. A reload uses the httpOnly
 *   refresh cookie under the same lock. Transient 5xx / timeout / network
 *   errors retry. Only a definitive refresh 401 clears local state.
 * - useUser() returns stable reference - access role via user.role, email via user.email
 * - For company data, use useCompany() hook
 * - activeCompanyId persisted to localStorage, sent as X-Company-Id header via apiClient interceptor
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../services/api/apiClient';
import {
  isSendableCompanyId,
  buildAuthMeSearch,
  readLiveCompanyScope,
  writeLiveCompanyScope,
  clearLiveCompanyScope
} from '../utils/sendableCompanyId';
import {
  setCompanyHeaderOverride,
  clearCompanyHeaderOverride
} from '../services/api/companyHeader';
import {
  executeRefreshWithRetry,
  withAuthRefreshLock
} from '../utils/authRefresh';

// Create context
const AuthContext = createContext(null);

// Token expiry buffer (treat as expired 1 minute before actual expiry)
const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000;

// Proactive refresh buffer — refresh 5 minutes before expiry.
// Prevents the 401-triggered refresh cycle that can cause visible page redirects
// when cross-site cookies are blocked by browser privacy features (Safari ITP,
// Chrome 3rd-party cookie restrictions). This is the standard OAuth pattern
// that Clerk handled internally before the Wristband migration.
const PROACTIVE_REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * AuthProvider - Manages authentication state
 *
 * Flow on mount:
 * 1. Check sessionStorage for existing access token
 * 2. If found and not expired, fetch user info
 * 3. If no token or expired, try to refresh from httpOnly cookie
 * 4. If refresh fails, user is not authenticated
 */
export function AuthProvider({ children }) {
  // Auth state
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [salesOrganization, setSalesOrganization] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);

  // Multi-company state
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(() => {
    const stored = localStorage.getItem('activeCompanyId');
    return isSendableCompanyId(stored) ? stored : null;
  });
  const [companyScope, setCompanyScope] = useState(null);
  const [defaultCompanyScope, setDefaultCompanyScope] = useState(null);
  const [defaultCompanyId, setDefaultCompanyId] = useState(null);
  const persistLocalCompanyRef = useRef(true);

  // Refs to track initialization and prevent race conditions
  const initializingRef = useRef(false);
  // Store the refresh promise to allow multiple callers to await the same refresh
  const refreshPromiseRef = useRef(null);

  /**
   * Store access token in memory and sessionStorage
   * Session storage survives page refresh but not tab close
   */
  const storeToken = useCallback((token, expiresIn) => {
    setAccessToken(token);

    // Calculate expiry time
    const expiresAt = Date.now() + (expiresIn * 1000);
    setTokenExpiresAt(expiresAt);

    // Store in sessionStorage for page refresh persistence
    sessionStorage.setItem('access_token', token);
    sessionStorage.setItem('token_expires_at', expiresAt.toString());

    // Set Authorization header for apiClient
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, []);

  /**
   * Clear all auth state
   */
  const clearAuthState = useCallback(() => {
    setUser(null);
    setCompany(null);
    setEmployee(null);
    setSalesOrganization(null);
    setAccessToken(null);
    setTokenExpiresAt(null);
    setIsSignedIn(false);
    setCompanies([]);
    setActiveCompanyId(null);
    setCompanyScope(null);
    setDefaultCompanyScope(null);
    setDefaultCompanyId(null);
    persistLocalCompanyRef.current = true;
    clearCompanyHeaderOverride();

    // Clear sessionStorage
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('token_expires_at');
    sessionStorage.removeItem('wristband_auth_request_token');
    clearLiveCompanyScope();

    // Clear multi-company persistence
    localStorage.removeItem('activeCompanyId');

    // Clear Authorization header
    delete apiClient.defaults.headers.common['Authorization'];
  }, []);

  /**
   * Fetch user info from /api/auth/me
   * Requires valid access token in Authorization header.
   *
   * @param {number|null} companyId - Live session UUID, or null.
   *   Login (no refresh): pack ignores leftover localStorage; LynxPay may use it.
   *   Refresh: send ?scope=all or ?companyId= — default_* does not win mid-session.
   *   Same-tab reload: sessionStorage live scope (UUID or all), not the star.
   */
  const fetchUserInfo = useCallback(async (companyId = null, options = {}) => {
    try {
      const scopeAll = options.scope === 'all';
      const isRefresh = options.refresh === true;
      const leftoverCompanyId = localStorage.getItem('activeCompanyId');

      // B3: leftover localStorage must not win pack login. Clear/ignore before first resolve.
      if (!isRefresh && !scopeAll && !isSendableCompanyId(companyId) && leftoverCompanyId) {
        try {
          const packRes = await apiClient.get('/features/pack-chrome');
          if (packRes.data?.data?.packs?.towtechSoChrome) {
            persistLocalCompanyRef.current = false;
            localStorage.removeItem('activeCompanyId');
          }
        } catch (packErr) {
          console.warn('Failed to resolve pack chrome before /me:', packErr);
        }
      }

      const search = buildAuthMeSearch({
        companyId,
        scope: scopeAll ? 'all' : null,
        refresh: isRefresh,
        leftoverCompanyId: localStorage.getItem('activeCompanyId'),
        useLeftover: persistLocalCompanyRef.current
      });
      const url = search ? `/auth/me?${search}` : '/auth/me';
      const response = await apiClient.get(url);

      if (response.data.success) {
        setUser(response.data.user);
        setCompany(response.data.company);
        setEmployee(response.data.employee);
        setSalesOrganization(response.data.salesOrganization);
        setCompanies(response.data.companies || []);
        setIsSignedIn(true);
        setCompanyScope(response.data.companyScope || (response.data.company ? 'company' : null));
        setDefaultCompanyScope(response.data.defaultCompanyScope || null);
        setDefaultCompanyId(response.data.defaultCompanyId || null);

        const resolvedCompanyId = response.data.company?.id || null;
        const isSoUser = response.data.user?.role === 'so';
        if (isSoUser) {
          persistLocalCompanyRef.current = false;
          localStorage.removeItem('activeCompanyId');
          setCompanyHeaderOverride(
            response.data.companyScope === 'all' ? null : resolvedCompanyId
          );
          setActiveCompanyId(resolvedCompanyId);
          writeLiveCompanyScope({
            scope: response.data.companyScope === 'all' ? 'all' : 'company',
            companyId: resolvedCompanyId
          });
        } else if (resolvedCompanyId) {
          setActiveCompanyId(resolvedCompanyId);
          localStorage.setItem('activeCompanyId', resolvedCompanyId.toString());
        } else {
          setActiveCompanyId(null);
          localStorage.removeItem('activeCompanyId');
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      return false;
    }
  }, []);

  /**
   * Refresh access token using httpOnly refresh token cookie.
   *
   * In-tab callers share refreshPromiseRef. Across tabs, withAuthRefreshLock
   * lets one tab POST /auth/refresh. Waiting tabs reuse the BroadcastChannel
   * message so Wristband refresh-token rotation is not raced. A reload POSTs
   * again and the browser sends the httpOnly cookie.
   *
   * Transient 5xx, timeout, and network errors retry inside the lock.
   * clearAuthState runs only for a definitive 401: AUTH_NO_REFRESH_TOKEN,
   * AUTH_REFRESH_FAILED, or Wristband invalid_grant.
   */
  const refreshToken = useCallback(async () => {
    // If a refresh is already in progress in this tab, wait for it
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    // Create a new refresh promise that all concurrent callers will share
    refreshPromiseRef.current = (async () => {
      try {
        const result = await withAuthRefreshLock(() => executeRefreshWithRetry({
          postRefresh: () => apiClient.post('/auth/refresh')
        }));

        if (result?.ok && result.accessToken) {
          storeToken(result.accessToken, result.expiresIn);
          return true;
        }

        if (result?.definitive) {
          clearAuthState();
          return false;
        }

        if (result?.error) {
          console.error('Token refresh failed:', result.error);
        }
        return false;
      } catch (error) {
        console.error('Token refresh failed:', error);
        return false;
      } finally {
        // Clear the promise ref so future refreshes can proceed
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [storeToken, clearAuthState]);

  /**
   * Check if token is expired or about to expire
   */
  const isTokenExpired = useCallback(() => {
    if (!tokenExpiresAt) return true;
    return Date.now() >= (tokenExpiresAt - TOKEN_EXPIRY_BUFFER_MS);
  }, [tokenExpiresAt]);

  /**
   * Get current access token, refreshing if needed
   */
  const getToken = useCallback(async () => {
    // First check current state
    if (accessToken && !isTokenExpired()) {
      return accessToken;
    }

    // Try to get from sessionStorage
    const storedToken = sessionStorage.getItem('access_token');
    const storedExpiresAt = sessionStorage.getItem('token_expires_at');

    if (storedToken && storedExpiresAt) {
      const expiresAt = parseInt(storedExpiresAt, 10);
      if (Date.now() < expiresAt - TOKEN_EXPIRY_BUFFER_MS) {
        // Token still valid
        setAccessToken(storedToken);
        setTokenExpiresAt(expiresAt);
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        return storedToken;
      }
    }

    // Token expired or missing - try refresh
    const refreshed = await refreshToken();
    if (refreshed) {
      return sessionStorage.getItem('access_token');
    }

    return null;
  }, [accessToken, isTokenExpired, refreshToken]);

  /**
   * Refresh user context (user, company, employee) from backend
   * Used after onboarding when company is created
   * Does not refresh the token, just re-fetches user info
   */
  const refreshContext = useCallback(async () => {
    const liveScope = companyScope === 'all' ? 'all' : null;
    const liveCompanyId = isSendableCompanyId(activeCompanyId) ? activeCompanyId : null;
    const refreshOpts = { refresh: true, scope: liveScope };
    try {
      await apiClient.post('/auth/refresh-context');
      return fetchUserInfo(liveCompanyId, refreshOpts);
    } catch (error) {
      console.error('Failed to refresh context:', error);
      try {
        return fetchUserInfo(liveCompanyId, refreshOpts);
      } catch (fetchError) {
        console.error('Failed to fetch user info:', fetchError);
        return false;
      }
    }
  }, [fetchUserInfo, companyScope, activeCompanyId]);

  /**
   * Immediately merge Company Settings updates into AuthContext company state.
   * Used after PUT /companies/me so Create Invoice/Estimate From does not wait
   * on (or get stuck behind a failed) refreshContext round-trip.
   *
   * @param {object} companyUpdate - Company payload from updateCompany response
   */
  const applyCompanyUpdate = useCallback((companyUpdate) => {
    if (!companyUpdate || typeof companyUpdate !== 'object') return;

    setCompany((prev) => {
      if (!prev) return companyUpdate;
      if (companyUpdate.id && prev.id && companyUpdate.id !== prev.id) {
        return prev;
      }
      return { ...prev, ...companyUpdate };
    });

    setCompanies((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const targetId = companyUpdate.id;
      return prev.map((entry) => {
        if (targetId && entry.id !== targetId) return entry;
        if (!targetId && prev[0] && entry.id !== prev[0].id) return entry;
        return { ...entry, ...companyUpdate };
      });
    });
  }, []);

  /**
   * Switch active company context for multi-company users.
   *
   * Flow:
   * 1. Update localStorage so apiClient interceptor sends new X-Company-Id
   * 2. Clear backend permission cache (stale for old company)
   * 3. Re-fetch user info scoped to the new company
   *
   * @param {number} companyId - Database company ID to switch to
   * @returns {Promise<boolean>} True if switch succeeded
   */
  const switchCompany = useCallback(async (companyId) => {
    const toAll = companyId == null || String(companyId).toLowerCase() === 'all';
    if (toAll) {
      if (companyScope === 'all' && !activeCompanyId) return true;
      try {
        localStorage.removeItem('activeCompanyId');
        setCompanyHeaderOverride(null);
        await apiClient.post('/auth/refresh-context');
        return fetchUserInfo(null, { scope: 'all' });
      } catch (error) {
        console.error('Failed to switch to All:', error);
        return false;
      }
    }

    if (!isSendableCompanyId(companyId)) return false;
    if (companyId === activeCompanyId) return true;

    try {
      if (persistLocalCompanyRef.current) {
        localStorage.setItem('activeCompanyId', companyId.toString());
        clearCompanyHeaderOverride();
      } else {
        localStorage.removeItem('activeCompanyId');
        setCompanyHeaderOverride(companyId);
      }

      await apiClient.post('/auth/refresh-context');
      const success = await fetchUserInfo(companyId);
      return success;
    } catch (error) {
      console.error('Failed to switch company:', error);
      return false;
    }
  }, [activeCompanyId, companyScope, fetchUserInfo]);

  /**
   * Persist default-on-login. Not localStorage.
   *
   * @param {{ scope: 'all'|'company', companyId?: string|null }} input
   */
  const setDefaultCompany = useCallback(async ({ scope, companyId = null }) => {
    const body = scope === 'all'
      ? { scope: 'all' }
      : { scope: 'company', companyId };
    const response = await apiClient.patch('/auth/default-company', body);
    if (response.data?.success) {
      setDefaultCompanyScope(response.data.defaultCompanyScope || scope);
      setDefaultCompanyId(response.data.defaultCompanyId || (scope === 'company' ? companyId : null));
      return true;
    }
    return false;
  }, []);

  /**
   * Handle OAuth callback - store token and fetch user info
   *
   * Called by AuthCallback component after receiving tokens from OAuth flow.
   * This ensures auth state is properly set BEFORE navigation to dashboard,
   * eliminating the race condition where initializeAuth runs before tokens are stored.
   *
   * @param {string} token - Access token from OAuth callback
   * @param {number} expiresIn - Token expiry in seconds
   * @returns {Promise<boolean>} - True if auth successful, false otherwise
   */
  const handleAuthCallback = useCallback(async (token, expiresIn) => {
    try {
      // Store token and set Authorization header
      storeToken(token, expiresIn);

      // New login applies default_*. Same-tab leftover live scope must not win.
      clearLiveCompanyScope();
      const success = await fetchUserInfo();

      if (success) {
        setIsLoaded(true);
        return true;
      }

      // User fetch failed - clear state
      clearAuthState();
      setIsLoaded(true);
      return false;
    } catch (error) {
      console.error('Auth callback error:', error);
      clearAuthState();
      setIsLoaded(true);
      return false;
    }
  }, [storeToken, fetchUserInfo, clearAuthState]);

  /**
   * Sign out - clear local state and redirect to Wristband logout
   *
   * Per Wristband docs, proper logout requires redirecting to Wristband's
   * /api/v1/logout endpoint on the tenant vanity domain. This:
   * 1. Clears the Wristband `sid` cookie (their session)
   * 2. Redirects back to our /sign-in page
   *
   * Flow:
   * 1. Call backend logout (revokes refresh token, clears our cookies)
   * 2. Clear frontend state (sessionStorage, context)
   * 3. Redirect to Wristband logout URL (clears their session)
   * 4. Wristband redirects to /sign-in (user fully logged out)
   */
  const signOut = useCallback(async () => {
    try {
      const response = await apiClient.post('/auth/logout');

      // Get the Wristband logout URL BEFORE clearing state
      // Backend returns logoutUrl pointing to Wristband's /api/v1/logout
      const logoutUrl = response.data.logoutUrl;

      if (logoutUrl) {
        // IMPORTANT: Clear sessionStorage BEFORE redirect to Wristband logout
        // sessionStorage persists across page navigations, so if we don't clear it,
        // when the user returns to /sign-in, AuthContext will find the old access token
        // and attempt to use it (which may succeed if not yet expired).
        //
        // Flow:
        // 1. Clear sessionStorage (access_token, token_expires_at, auth request token)
        // 2. Redirect to Wristband logout URL (tenant vanity domain)
        // 3. Wristband clears sid cookie
        // 4. Wristband redirects to our /sign-in
        // 5. SignIn component sees no session, starts fresh OAuth flow
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('token_expires_at');
        sessionStorage.removeItem('wristband_auth_request_token');
        clearLiveCompanyScope();
        window.location.href = logoutUrl;
      } else {
        // Fallback if no logout URL (shouldn't happen)
        clearAuthState();
        window.location.href = '/sign-in';
      }
    } catch (error) {
      clearAuthState();
      // On error, just redirect to sign-in
      window.location.href = '/sign-in';
    }
  }, [clearAuthState]);

  /**
   * Initialize auth state on mount.
   *
   * Wrapped in initializeWithSafety() which adds a 10-second timeout safety net.
   * If auth initialization never resolves (network hang, CDN failure, QUIC error),
   * the timeout force-clears the stale session and sets isLoaded=true, allowing
   * ProtectedRoute to redirect to /sign-in. It must not do that while
   * refreshPromiseRef is in flight: a lock wait or a 5xx retry can outlast 10s,
   * and signing out early mounts SignIn so a late success navigates to
   * /dashboard. The finally block guarantees isLoaded is always set once the
   * in-flight refresh settles — no code path can leave the app in a permanent
   * loading state when that promise finishes.
   *
   * Matches banking-standard session timeout behavior: expired sessions redirect
   * cleanly, never hang.
   */
  useEffect(() => {
    let authTimeoutId = null;

    const initializeAuth = async () => {
      // Check for access token in sessionStorage
      const storedToken = sessionStorage.getItem('access_token');
      const storedExpiresAt = sessionStorage.getItem('token_expires_at');

      if (storedToken && storedExpiresAt) {
        const expiresAt = parseInt(storedExpiresAt, 10);

        // Check if token is still valid
        if (Date.now() < expiresAt - TOKEN_EXPIRY_BUFFER_MS) {
          // Token still valid - use it
          setAccessToken(storedToken);
          setTokenExpiresAt(expiresAt);
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

          const live = readLiveCompanyScope();
          const success = await fetchUserInfo(
            live.companyId,
            live.scope === 'all' ? { scope: 'all' } : {}
          );
          if (success) {
            return; // Auth resolved — finally block handles isLoaded + ref cleanup
          }
        }
      }

      // No valid token in sessionStorage - try to refresh from cookie
      const refreshed = await refreshToken();

      if (refreshed) {
        const live = readLiveCompanyScope();
        const success = await fetchUserInfo(
          live.companyId,
          live.scope === 'all' ? { scope: 'all' } : {}
        );
        if (success) {
          return; // Auth resolved — finally block handles isLoaded + ref cleanup
        }
        clearAuthState();
        return;
      }

      // Transient refresh failure keeps any access token so a later retry can
      // recover. A definitive 401 already cleared storage inside refreshToken.
      if (!sessionStorage.getItem('access_token')) {
        clearAuthState();
      }
    };

    const initializeWithSafety = async () => {
      // Prevent concurrent initialization
      if (initializingRef.current) return;

      // Skip initialization on /auth/callback — that page handles its own auth
      // via handleAuthCallback(). Running initializeAuth here would race:
      // initializeAuth finds no session → clearAuthState() wipes the token that
      // handleAuthCallback just stored → user redirected to /sign-in → loop.
      if (window.location.pathname.endsWith('/auth/callback') && window.location.hash.includes('access_token')) {
        setIsLoaded(true);
        return;
      }

      initializingRef.current = true;

      try {
        // Safety net: force-clear stale session if auth never resolves within 10 seconds.
        // Covers network hangs, CDN failures, and QUIC protocol errors that prevent
        // the refresh request from completing. Normal auth resolves in < 2 seconds.
        authTimeoutId = setTimeout(() => {
          if (!initializingRef.current) return;
          // Lock wait and refresh retries can outlast 10s. Clearing here sets
          // isLoaded while signed out, SignIn mounts, and the late success
          // navigates to /dashboard. The boot is already awaiting this promise.
          if (refreshPromiseRef.current) return;
          console.warn('[Auth] Initialization timed out after 10s — clearing stale session');
          clearAuthState();
          setIsLoaded(true);
          initializingRef.current = false;
        }, 10000);

        await initializeAuth();

      } catch (error) {
        console.error('[Auth] Initialization failed:', error);
        clearAuthState();
      } finally {
        // Guarantee cleanup — no code path can leave isLoaded as false
        if (authTimeoutId) {
          clearTimeout(authTimeoutId);
          authTimeoutId = null;
        }
        if (initializingRef.current) {
          setIsLoaded(true);
          initializingRef.current = false;
        }
      }
    };

    initializeWithSafety();

    return () => {
      if (authTimeoutId) {
        clearTimeout(authTimeoutId);
      }
    };
  }, [fetchUserInfo, refreshToken, clearAuthState]);

  /**
   * Set up 401 interceptor for automatic token refresh
   */
  useEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't retry auth endpoints or already retried requests
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/')
        ) {
          originalRequest._retry = true;

          // Try to refresh token
          const refreshed = await refreshToken();

          if (refreshed) {
            // Retry with new token
            const newToken = sessionStorage.getItem('access_token');
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }

          // Definitive 401 already cleared local state inside refreshToken.
          // A transient failure must not log the user out.
        }

        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [refreshToken]);

  /**
   * Proactive token refresh timer.
   *
   * Schedules a silent refresh 5 minutes before the access token expires,
   * while the current token is still valid. This prevents the expired-token
   * scenario where the 401 interceptor must rely on cross-site cookies
   * (which browsers increasingly block), avoiding the visible redirect
   * cycle that looks like a page refresh.
   *
   * The 401 interceptor remains as a defense-in-depth fallback.
   */
  useEffect(() => {
    if (!tokenExpiresAt || !isSignedIn) return;

    const msUntilRefresh = tokenExpiresAt - Date.now() - PROACTIVE_REFRESH_BUFFER_MS;

    // If already past the refresh window, refresh immediately
    if (msUntilRefresh <= 0) {
      refreshToken();
      return;
    }

    const timerId = setTimeout(async () => {
      const success = await refreshToken();
      if (!success) {
        console.warn('[Auth] Proactive token refresh failed — will retry on next API call via 401 interceptor');
      }
    }, msUntilRefresh);

    return () => clearTimeout(timerId);
  }, [tokenExpiresAt, isSignedIn, refreshToken]);

  // Context value
  const value = {
    user,
    company,
    employee,
    salesOrganization,
    isSignedIn,
    isLoaded,
    getToken,
    signOut,
    refreshContext,
    applyCompanyUpdate,
    handleAuthCallback,
    accessToken,
    // Multi-company
    companies,
    activeCompanyId,
    companyScope,
    defaultCompanyScope,
    defaultCompanyId,
    switchCompany,
    setDefaultCompany,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth - Hook to access auth state and methods
 *
 * Returns:
 * - user: same /auth/me users row stored on context (firstName, lastName, email, role)
 * - salesOrganization: same /me salesOrganization object
 * - isSignedIn: Boolean indicating if user is authenticated
 * - isLoaded: Boolean indicating if auth state has been initialized
 * - userId: Database user ID (for backward compatibility)
 * - sessionId: Wristband user ID (for backward compatibility)
 * - getToken: Function to get current access token (refreshes if needed)
 * - signOut: Function to sign out user
 *
 * @notes Pack rail person card reads useAuth().user. Omitting user left the
 *   card on "?" after /me had already hydrated context.user (LYN-361).
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return {
    user: context.user,
    salesOrganization: context.salesOrganization,
    isSignedIn: context.isSignedIn,
    isLoaded: context.isLoaded,
    userId: context.user?.id || null,
    sessionId: context.user?.wristbandUserId || null,
    getToken: context.getToken,
    signOut: context.signOut,
    refreshContext: context.refreshContext,
    applyCompanyUpdate: context.applyCompanyUpdate,
    // Multi-company
    companies: context.companies,
    activeCompanyId: context.activeCompanyId,
    companyScope: context.companyScope,
    defaultCompanyScope: context.defaultCompanyScope,
    defaultCompanyId: context.defaultCompanyId,
    switchCompany: context.switchCompany,
    setDefaultCompany: context.setDefaultCompany,
  };
}

/**
 * useUser - Hook to access user object
 * Returns stable reference from context (no transformation)
 *
 * Returns:
 * - isSignedIn: Boolean indicating if user is authenticated
 * - isLoaded: Boolean indicating if auth state has been initialized
 * - user: User object from backend (id, email, firstName, lastName, role, wristbandUserId)
 *
 * @notes
 * - Access role via user.role (NOT user.publicMetadata.role)
 * - Access email via user.email (NOT user.primaryEmailAddress.emailAddress)
 * - For company data, use useCompany() hook instead
 */
export function useUser() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useUser must be used within an AuthProvider');
  }

  return {
    isSignedIn: context.isSignedIn,
    isLoaded: context.isLoaded,
    user: context.user,
  };
}

/**
 * useCompany - Hook to access company context
 */
export function useCompany() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useCompany must be used within an AuthProvider');
  }

  return {
    company: context.company,
    isLoaded: context.isLoaded,
    activeCompanyId: context.activeCompanyId,
  };
}

/**
 * useEmployee - Hook to access employee context
 */
export function useEmployee() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useEmployee must be used within an AuthProvider');
  }

  return {
    employee: context.employee,
    isLoaded: context.isLoaded,
  };
}

/**
 * useSalesOrganization - Hook to access sales organization context (SO users only)
 */
export function useSalesOrganization() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useSalesOrganization must be used within an AuthProvider');
  }

  return {
    salesOrganization: context.salesOrganization,
    isLoaded: context.isLoaded,
  };
}

/**
 * SignedIn - Component that renders children only if signed in
 */
export function SignedIn({ children }) {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) return null;
  if (!isSignedIn) return null;

  return <>{children}</>;
}

/**
 * SignedOut - Component that renders children only if signed out
 */
export function SignedOut({ children }) {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) return null;
  if (isSignedIn) return null;

  return <>{children}</>;
}

/**
 * SignOutButton - Button component for sign out
 *
 * @param {React.ReactNode} children - Button text/content
 * @param {string} className - CSS classes for styling
 * @param {Function} onClick - Additional onClick handler (runs before signOut)
 */
export function SignOutButton({ children, className, onClick }) {
  const { signOut } = useAuth();

  const handleClick = async () => {
    // Run any additional onClick handler first
    if (onClick) {
      onClick();
    }
    await signOut();
  };

  return (
    <button onClick={handleClick} type="button" className={className}>
      {children || 'Sign Out'}
    </button>
  );
}

/**
 * UserProfile - Stub component for user profile
 */
export function UserProfile() {
  const { user } = useUser();

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <p>User Profile</p>
      {user && (
        <div>
          <p>{`${user.firstName || ''} ${user.lastName || ''}`.trim()}</p>
          <p>{user.email}</p>
        </div>
      )}
    </div>
  );
}

export { AuthContext };
export default AuthContext;
